/*
 * Swee Choon catering service-charge guard.
 *
 * The Upcharge Fees app correctly builds a 10% CART fee when a mixed cart is
 * created in one request, but can retain a stale fee basis when a regular
 * retail item is added after the catering fee already exists. This guard
 * keeps the fee carrier's exclusion list aligned with product tag
 * `buffet-service-charge-eligible`, then lets Shopify's cart transform
 * recalculate the fee. It never changes merchandise quantities or prices.
 */
(function () {
  'use strict';

  var ELIGIBLE_TAG = 'buffet-service-charge-eligible';
  var WRAPPER_VARIANT_ID = 50528086720664;
  var SERVICE_FEE_ID = 10321009737880;
  var PRODUCT_TAG_CACHE = Object.create(null);
  var timer = null;
  var repairing = false;
  var nativeFetch = window.fetch.bind(window);

  function uniqueSortedNumbers(values) {
    return Array.from(new Set(values.map(Number).filter(Number.isFinite))).sort(function (a, b) {
      return a - b;
    });
  }

  function sameNumbers(a, b) {
    var left = uniqueSortedNumbers(Array.isArray(a) ? a : []);
    var right = uniqueSortedNumbers(Array.isArray(b) ? b : []);
    return left.length === right.length && left.every(function (value, index) {
      return value === right[index];
    });
  }

  function tagsFromConfig(productId) {
    try {
      var tags = window.mwFeesConfig &&
        window.mwFeesConfig.cart &&
        window.mwFeesConfig.cart.tags &&
        window.mwFeesConfig.cart.tags[String(productId)];
      return Array.isArray(tags) ? tags : null;
    } catch (e) {
      return null;
    }
  }

  function loadProductTags(item) {
    var productId = String(item.product_id || '');
    var configured = tagsFromConfig(productId);
    if (configured) {
      PRODUCT_TAG_CACHE[productId] = configured;
      return Promise.resolve(configured);
    }
    if (PRODUCT_TAG_CACHE[productId]) return Promise.resolve(PRODUCT_TAG_CACHE[productId]);
    if (!item.handle) return Promise.resolve([]);

    return nativeFetch('/products/' + encodeURIComponent(item.handle) + '.js', {
      credentials: 'same-origin',
      cache: 'no-store'
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Product lookup failed');
        return response.json();
      })
      .then(function (product) {
        var tags = Array.isArray(product.tags)
          ? product.tags
          : (typeof product.tags === 'string' ? product.tags.split(',').map(function (tag) { return tag.trim(); }) : []);
        PRODUCT_TAG_CACHE[productId] = tags;
        return tags;
      })
      .catch(function () {
        // Fail closed: an unverified product is excluded from the catering fee.
        return [];
      });
  }

  function feeCarrier(item) {
    return item && Number(item.variant_id) === WRAPPER_VARIANT_ID;
  }

  function serviceFeeProperty(item) {
    var entries = item && item.properties && item.properties.__mws_fee_cta;
    if (!Array.isArray(entries)) return null;
    return entries.find(function (entry) {
      return entry && Number(entry.id) === SERVICE_FEE_ID && entry.type === 'CART';
    }) || null;
  }

  function schedule(delay) {
    clearTimeout(timer);
    timer = setTimeout(reconcile, typeof delay === 'number' ? delay : 900);
  }

  function reconcile() {
    if (repairing) return;

    nativeFetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        var feeLine = items.find(feeCarrier);
        if (!feeLine) return null;

        var merchandise = items.filter(function (item) {
          return !feeCarrier(item) && item.product_type !== 'mws_fee_generated';
        });

        return Promise.all(merchandise.map(function (item) {
          return loadProductTags(item).then(function (tags) {
            return tags.indexOf(ELIGIBLE_TAG) === -1 ? Number(item.variant_id) : null;
          });
        })).then(function (results) {
          var expectedExclusions = uniqueSortedNumbers(results.filter(Number.isFinite));
          var currentFee = serviceFeeProperty(feeLine);
          var currentExclusions = currentFee && Array.isArray(currentFee.excl) ? currentFee.excl : [];
          if (sameNumbers(currentExclusions, expectedExclusions)) return null;

          var properties = Object.assign({}, feeLine.properties || {});
          var entries = Array.isArray(properties.__mws_fee_cta)
            ? properties.__mws_fee_cta.map(function (entry) { return Object.assign({}, entry); })
            : [];
          var matched = false;
          entries = entries.map(function (entry) {
            if (!entry || Number(entry.id) !== SERVICE_FEE_ID || entry.type !== 'CART') return entry;
            matched = true;
            if (expectedExclusions.length) entry.excl = expectedExclusions;
            else delete entry.excl;
            return entry;
          });
          if (!matched) return null;

          properties.__mws_fee_cta = entries;
          repairing = true;
          return nativeFetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: feeLine.key,
              quantity: feeLine.quantity || 1,
              properties: properties
            })
          }).then(function (response) {
            if (!response.ok) throw new Error('Service-charge recalculation failed');
            return response.json();
          }).then(function () {
            // Reload once so every cart surface reflects Shopify's corrected total.
            window.location.reload();
          }).catch(function (error) {
            repairing = false;
            console.warn('[service-charge-guard]', error);
          });
        });
      })
      .catch(function (error) {
        console.warn('[service-charge-guard]', error);
      });
  }

  // Observe storefront cart mutations, including add-to-cart actions from
  // product pages and updates inside the AJAX cart drawer.
  var interceptedFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : ((input && input.url) || '');
    var response = interceptedFetch.apply(this, arguments);
    if (!repairing && /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i.test(url)) {
      response.then(function (result) {
        if (result && result.ok) {
          schedule(700);
          setTimeout(function () { schedule(0); }, 2400);
        }
      }).catch(function () {});
    }
    return response;
  };

  document.addEventListener('cart.requestComplete', function () { schedule(400); });
  document.addEventListener('DOMContentLoaded', function () {
    schedule(1200);
    setTimeout(function () { schedule(0); }, 3500);
  });
})();
